import { useState } from 'react';
import { motion } from 'framer-motion';
import { ThreadPrimitive, ComposerPrimitive, useAuiState } from '@assistant-ui/react';
import { MinervaRuntimeProvider } from './MinervaRuntime';
import { useMinervaContext } from './MinervaContext';
import MinervaMark from './MinervaMark';
import MarkdownView from './MarkdownView';
import CitationsBlock from './CitationsBlock';
import FeedbackButtons from './FeedbackButtons';
import MetaBadges from './MetaBadges';
import type { ResponseMode } from '../../../services/api';

/* ─── Streaming config ────────────────────────────────────────────────────── */
// (Streaming speed is configured in MinervaRuntime.tsx)

/* Muelle de entrada compartido para los mensajes */
const MSG_SPRING = { type: 'spring', stiffness: 120, damping: 20 } as const;

/* ─── Styles ──────────────────────────────────────────────────────────────── */
function ChatStyles() {
  return (
    <style>{`
      /* Layout */
      .chat-root {
        background: transparent;
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow: hidden;
        min-width: 0;
        position: relative;
      }
      .chat-scroll {
        flex: 1;
        overflow-y: auto;
        padding: 40px 0 8px;
        scroll-behavior: smooth;
      }
      .chat-inner {
        max-width: 720px;
        margin: 0 auto;
        padding: 0 32px;
        display: flex;
        flex-direction: column;
        gap: 38px;
      }

      /* ── Mensaje del usuario: nota al margen, burbuja sobria ── */
      .msg-user {
        display: flex;
        justify-content: flex-end;
      }
      .msg-user-bubble {
        background: var(--ink-750);
        color: var(--vellum);
        border: 1px solid var(--gilt-line);
        border-radius: 14px 14px 3px 14px;
        padding: 11px 16px;
        font-family: var(--font-ui);
        font-size: 0.92rem;
        font-weight: 400;
        line-height: 1.55;
        max-width: 78%;
        white-space: pre-wrap;
        word-break: break-word;
        box-shadow: var(--shadow-ink-sm);
      }

      /* ── Respuesta de Minerva: texto escribado, sin caja ── */
      .msg-minerva {
        display: flex;
        gap: 18px;
        padding-left: 2px;
      }
      .msg-minerva-rail {
        flex-shrink: 0;
        width: 26px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
      }
      .msg-minerva-mark {
        opacity: 0.9;
      }
      /* filete vertical de oro que recorre la respuesta */
      .msg-minerva-rule {
        flex: 1;
        width: 1px;
        background: linear-gradient(to bottom, var(--gilt-line), transparent 92%);
      }
      .msg-minerva-content { flex: 1; min-width: 0; }
      .msg-minerva-attr {
        font-family: var(--font-display);
        font-style: italic;
        font-size: 0.95rem;
        font-weight: 500;
        color: var(--gilt-bright);
        letter-spacing: 0.01em;
        margin-bottom: 9px;
      }

      /* Capitular iluminada sobre el primer párrafo de la respuesta renderizada */
      .minerva-prose > p:first-of-type::first-letter {
        font-family: var(--font-display);
        font-weight: 600;
        font-size: 3.1em;
        line-height: 0.78;
        float: left;
        padding: 4px 10px 0 0;
        color: var(--gilt);
        text-shadow: 0 1px 0 rgba(194,162,78,0.25);
      }

      /* ── Indicador de escritura: la tinta se asienta ── */
      .minerva-writing {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 4px 0 2px;
        color: var(--vellum-faint);
        font-family: var(--font-mono);
        font-size: 0.7rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      .ink-line {
        position: relative;
        height: 1px;
        width: 120px;
        background: var(--gilt-hair);
        overflow: hidden;
        border-radius: 1px;
      }
      .ink-line::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, transparent, var(--gilt), transparent);
        transform: translateX(-100%);
        animation: ink-sweep 1.4s cubic-bezier(0.45,0,0.55,1) infinite;
      }
      @keyframes ink-sweep {
        to { transform: translateX(100%); }
      }

      /* ── Pseudo-streaming: el markdown parcial ya va estilado; solo se añade
         el cursor al final del último bloque ── */
      .msg-streaming:empty::after,
      .msg-streaming > :not(ul):not(ol):not(pre):last-child::after,
      .msg-streaming > :is(ul, ol):last-child > li:last-child::after {
        content: '';
        display: inline-block;
        width: 2px; height: 1.05em;
        background: var(--gilt);
        vertical-align: text-bottom;
        border-radius: 1px;
        margin-left: 2px;
        animation: blink-cursor 0.9s step-end infinite;
      }
      @keyframes blink-cursor { 0%,100% { opacity: 1; } 50% { opacity: 0; } }

      /* ── Zona de entrada ── */
      .chat-input-area {
        background: linear-gradient(to top, var(--ink-900) 72%, transparent);
        padding: 14px 32px 22px;
        position: relative;
        z-index: 1;
      }
      .chat-composer {
        max-width: 720px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        background: var(--ink-800);
        border: 1px solid var(--gilt-line);
        border-top: 1px solid var(--gilt);
        border-radius: 4px 4px 13px 13px;
        padding: 4px 6px 6px;
        box-shadow: var(--shadow-ink);
        transition: border-color 0.22s ease;
      }
      .chat-composer:focus-within { border-color: var(--gilt); }
      .chat-textarea {
        width: 100%;
        background: transparent;
        border: none;
        color: var(--vellum);
        caret-color: var(--gilt);
        font-family: var(--font-read);
        font-size: 1rem;
        padding: 12px 14px 6px;
        resize: none;
        min-height: 48px;
        max-height: 200px;
        line-height: 1.6;
        outline: none;
      }
      .chat-textarea::placeholder {
        color: var(--vellum-faint);
        font-style: italic;
      }
      .chat-composer-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 2px 4px;
        gap: 10px;
      }
      .chat-register {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        font-family: var(--font-mono);
        font-size: 0.64rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--vellum-faint);
      }
      .chat-mode-wrap { position: relative; display: inline-flex; align-items: center; }
      .chat-mode-select {
        appearance: none;
        background: var(--ink-750);
        border: 1px solid var(--gilt-hair);
        color: var(--vellum-dim);
        border-radius: 7px;
        font-family: var(--font-mono);
        font-size: 0.7rem;
        letter-spacing: 0.02em;
        padding: 6px 26px 6px 9px;
        outline: none;
        cursor: pointer;
        transition: border-color 0.18s ease, color 0.18s ease;
      }
      .chat-mode-select:hover { border-color: var(--gilt-line); color: var(--vellum); }
      .chat-mode-wrap::after {
        content: '\\ea4e';
        font-family: 'remixicon';
        position: absolute;
        right: 8px;
        font-size: 0.85rem;
        color: var(--gilt);
        opacity: 0.7;
        pointer-events: none;
      }
      .btn-send {
        background: var(--gilt);
        color: var(--ink-900);
        border: none;
        border-radius: 9px;
        width: 38px; height: 38px;
        display: flex; align-items: center; justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
        font-size: 1.05rem;
        transition: transform 0.14s cubic-bezier(0.16,1,0.3,1), background 0.18s, box-shadow 0.18s;
      }
      .btn-send:hover:not(:disabled) {
        background: var(--gilt-bright);
        box-shadow: 0 4px 16px -4px rgba(194,162,78,0.5);
      }
      .btn-send:active:not(:disabled) { transform: scale(0.92); }
      .btn-send:disabled { opacity: 0.28; cursor: not-allowed; }
      .chat-hint {
        font-family: var(--font-mono);
        font-size: 0.62rem;
        color: var(--vellum-faint);
        text-align: center;
        margin-top: 9px;
        letter-spacing: 0.04em;
      }

      /* ── Empty state: códice abierto ── */
      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        padding: 60px 28px;
        text-align: center;
        min-height: 420px;
      }
      .empty-mark {
        position: relative;
        margin-bottom: 24px;
        animation: empty-float 6s ease-in-out infinite;
      }
      @keyframes empty-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
      .empty-mark::before {
        content: '';
        position: absolute;
        inset: -28px;
        background: radial-gradient(circle, rgba(194,162,78,0.12), transparent 70%);
        border-radius: 50%;
      }
      .empty-title {
        font-family: var(--font-display);
        font-size: 1.9rem;
        font-weight: 500;
        font-optical-sizing: auto;
        color: var(--vellum);
        margin-bottom: 10px;
        line-height: 1.15;
        letter-spacing: 0.01em;
      }
      .empty-title em { font-style: italic; color: var(--gilt-bright); }
      .empty-sub {
        font-family: var(--font-read);
        font-size: 1rem;
        color: var(--vellum-dim);
        line-height: 1.6;
        max-width: 380px;
      }
      .empty-rule {
        width: 52px; height: 1px;
        background: linear-gradient(90deg, transparent, var(--gilt), transparent);
        margin: 26px 0 22px;
      }
      .empty-starters {
        display: flex;
        flex-direction: column;
        gap: 11px;
        align-items: center;
      }
      .empty-starter {
        background: none;
        border: none;
        cursor: pointer;
        font-family: var(--font-read);
        font-size: 0.95rem;
        font-style: italic;
        color: var(--vellum-dim);
        padding: 2px 0;
        border-bottom: 1px solid var(--gilt-hair);
        transition: color 0.18s ease, border-color 0.18s ease;
      }
      .empty-starter:hover { color: var(--gilt-bright); border-bottom-color: var(--gilt); }

      @media (max-width: 768px) {
        .chat-inner { padding: 0 18px; }
        .chat-input-area { padding: 12px 16px 18px; }
      }
    `}</style>
  );
}

/* ─── Mensaje del usuario ──────────────────────────────────────────────────── */
function UserMessage() {
  const message = useAuiState((s) => s.message);
  const content = message.content[0]?.type === 'text' ? message.content[0].text : '';
  return (
    <motion.div
      className="msg-user"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={MSG_SPRING}
    >
      <div className="msg-user-bubble">{content}</div>
    </motion.div>
  );
}

/* ─── Markdown parcial durante el pseudo-streaming ─────────────────────────── */
// Cierra la sintaxis que el typewriter deja a medias (bloques de código, código
// inline, negrita) para que el texto parcial se renderice ya con su estilo final
// en lugar de mostrar marcadores sueltos.
function closePendingMarkdown(partial: string): string {
  // Marcadores a medio escribir al final (p. ej. el primer '*' de '**').
  let text = partial.replace(/[*`$]+$/, '');

  const fences = text.match(/^\s*```/gm)?.length ?? 0;
  if (fences % 2 === 1) return `${text}\n\`\`\``;

  // Fórmula $$…$$ sin cerrar: se oculta hasta que llegue su cierre en lugar de
  // mostrar LaTeX crudo.
  const mathDelims = text.replace(/```[\s\S]*?```/g, '').match(/\$\$/g)?.length ?? 0;
  if (mathDelims % 2 === 1) text = text.slice(0, text.lastIndexOf('$$'));

  const outsideFences = text.replace(/```[\s\S]*?```/g, '');
  const openCode = (outsideFences.match(/`/g)?.length ?? 0) % 2 === 1;
  const outsideCode = outsideFences.replace(/`[^`]*`/g, '').replace(/`[^`]*$/, '');
  const openBold = (outsideCode.match(/\*\*/g)?.length ?? 0) % 2 === 1;

  if (openCode) text += '`';
  // El cierre va antes del espacio final: en CommonMark un '**' precedido de
  // espacio no cierra la negrita.
  if (openBold) text = text.replace(/(\s*)$/, '**$1');
  return text;
}

/* ─── Carril izquierdo: marca + filete de oro ──────────────────────────────── */
function MinervaRail() {
  return (
    <div className="msg-minerva-rail" aria-hidden="true">
      <span className="msg-minerva-mark"><MinervaMark size={22} strokeWidth={1.4} /></span>
      <div className="msg-minerva-rule" />
    </div>
  );
}

/* ─── Respuesta de Minerva ─────────────────────────────────────────────────── */
function AssistantMessage() {
  const message = useAuiState((s) => s.message);
  const { extMessages, subject, ask } = useMinervaContext();

  // Mensaje fantasma en ejecución → indicador de escritura
  if (message.status.type === 'running') {
    return (
      <motion.div className="msg-minerva" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={MSG_SPRING}>
        <MinervaRail />
        <div className="msg-minerva-content">
          <p className="msg-minerva-attr">Minerva</p>
          <div className="minerva-writing">
            <span>Escribiendo</span>
            <span className="ink-line" />
          </div>
        </div>
      </motion.div>
    );
  }

  const extMsg = extMessages.find((m) => m.id === message.id);
  const isStreaming = extMsg?.isStreaming ?? false;
  const citations = extMsg?.citations;
  const followUps = extMsg?.followUpQuestions;
  const queryId = extMsg?.queryId;
  const content = message.content[0]?.type === 'text' ? message.content[0].text : '';

  return (
    <motion.div className="msg-minerva" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={MSG_SPRING}>
      <MinervaRail />
      <div className="msg-minerva-content">
        <p className="msg-minerva-attr">Minerva</p>

        {/* Mismo contenedor y mismo render durante y después del streaming:
            el texto aparece ya con su estilo y no se re-maqueta al terminar. */}
        <div className={`msg-assistant-body minerva-prose${isStreaming ? ' msg-streaming' : ''}`}>
          <MarkdownView content={isStreaming ? closePendingMarkdown(content) : content} />
        </div>

        {!isStreaming && (
          <>
            <MetaBadges confidence={extMsg?.confidence} reasoningQuality={extMsg?.reasoningQuality} />

            {citations && citations.length > 0 && <CitationsBlock citations={citations} />}

            {followUps && followUps.length > 0 && (
              <div className="followups-block">
                <p className="followups-label">
                  <i className="ri-quill-pen-line" /> Continúa explorando
                </p>
                <motion.div
                  className="followups-grid"
                  initial="hidden"
                  animate="show"
                  variants={{ show: { transition: { staggerChildren: 0.06 } } }}
                >
                  {followUps.map((q, i) => (
                    <motion.button
                      key={`${q}-${i}`}
                      type="button"
                      className="followup-chip"
                      onClick={() => ask(q)}
                      variants={{ hidden: { opacity: 0, x: -6 }, show: { opacity: 1, x: 0 } }}
                    >
                      {q}
                    </motion.button>
                  ))}
                </motion.div>
              </div>
            )}

            {queryId && <FeedbackButtons targetType="chat" targetId={queryId} subject={subject} />}
          </>
        )}
      </div>
    </motion.div>
  );
}

/* ─── Empty state ──────────────────────────────────────────────────────────── */
function EmptyState({ subject }: { subject: string }) {
  const { ask } = useMinervaContext();
  const starters = [
    `¿Cuáles son los conceptos clave de ${subject}?`,
    '¿Por dónde me conviene empezar a estudiar?',
    'Hazme un resumen de los temas principales.',
  ];
  return (
    <div className="empty-state">
      <div className="empty-mark">
        <MinervaMark size={58} strokeWidth={1.2} />
      </div>
      <p className="empty-title">
        El folio está <em>en blanco</em>
      </p>
      <p className="empty-sub">
        Pregunta lo que necesites sobre{' '}
        <span style={{ color: 'var(--gilt-bright)', fontStyle: 'italic' }}>{subject}</span>{' '}
        y Minerva responderá con sus referencias.
      </p>
      <div className="empty-rule" />
      <div className="empty-starters">
        {starters.map((q) => (
          <button key={q} type="button" className="empty-starter" onClick={() => ask(q)}>
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Componente principal ─────────────────────────────────────────────────── */
interface ChatWindowProps {
  subject: string;
  conversationId: string | null;
}

export default function ChatWindow({ subject }: ChatWindowProps) {
  const [responseMode, setResponseMode] = useState<ResponseMode>('adaptive');

  return (
    <MinervaRuntimeProvider subject={subject} responseMode={responseMode}>
      <ChatStyles />
      <ThreadPrimitive.Root className="chat-root">
        <ThreadPrimitive.Viewport className="chat-scroll">
          <div className="chat-inner">
            <ThreadPrimitive.Empty>
              <EmptyState subject={subject} />
            </ThreadPrimitive.Empty>

            <ThreadPrimitive.Messages components={{ UserMessage, AssistantMessage }} />
          </div>
        </ThreadPrimitive.Viewport>

        <div className="chat-input-area">
          <ComposerPrimitive.Root className="chat-composer">
            <ComposerPrimitive.Input
              className="chat-textarea"
              placeholder="Escribe tu pregunta…"
              rows={1}
            />
            <div className="chat-composer-footer">
              <span className="chat-register">
                Registro
                <span className="chat-mode-wrap">
                  <select
                    className="chat-mode-select"
                    value={responseMode}
                    onChange={(e) => setResponseMode(e.target.value as ResponseMode)}
                    aria-label="Registro de respuesta de Minerva"
                  >
                    <option value="adaptive">adaptativo</option>
                    <option value="concise">conciso</option>
                    <option value="deep">profundo</option>
                  </select>
                </span>
              </span>
              <ComposerPrimitive.Send className="btn-send" aria-label="Enviar mensaje">
                <i className="ri-quill-pen-line" />
              </ComposerPrimitive.Send>
            </div>
          </ComposerPrimitive.Root>
          <p className="chat-hint">Enter para enviar · Shift+Enter para nueva línea</p>
        </div>
      </ThreadPrimitive.Root>
    </MinervaRuntimeProvider>
  );
}
